import type { Control } from "../core/Control.js";
import { createFlare, Flare, type FlareContext, type FlareSpec } from "./Flare.js";

/**
 * The effects attached to one control.
 *
 * Owns the per-flare clock and the animator registration, so a control gains
 * animated decoration without knowing anything about frames.
 */
export class FlareList {
  #owner: Control;
  #items: Flare[] = [];
  #startedAt = new WeakMap<Flare, number>();
  #lastFrame = -1;
  #animating = false;

  constructor(owner: Control) {
    this.#owner = owner;
  }

  get length(): number {
    return this.#items.length;
  }

  [Symbol.iterator](): Iterator<Flare> {
    return this.#items[Symbol.iterator]();
  }

  toArray(): Flare[] {
    return [...this.#items];
  }

  /** Attach an effect, either built or described as data. */
  add<T extends Flare>(flare: T): T;
  add(spec: FlareSpec): Flare;
  add(flare: Flare | FlareSpec): Flare {
    const resolved = flare instanceof Flare ? flare : createFlare(flare);
    this.#items.push(resolved);
    resolved.onAttach(this.#owner);
    this.#sync();
    this.#owner.invalidate();
    return resolved;
  }

  addAll(flares: readonly (Flare | FlareSpec)[]): Flare[] {
    return flares.map((f) => this.add(f as FlareSpec));
  }

  remove(flare: Flare): void {
    const i = this.#items.indexOf(flare);
    if (i < 0) return;
    this.#items.splice(i, 1);
    this.#startedAt.delete(flare);
    flare.onDetach(this.#owner);
    this.#sync();
    this.#owner.invalidate();
  }

  /**
   * Swap one effect for another in the same place, which is also its place in the paint order.
   * Adds `next` at the end when `old` is not attached.
   */
  replace(old: Flare, next: Flare | FlareSpec): Flare {
    const i = this.#items.indexOf(old);
    if (i < 0) return this.add(next as FlareSpec);
    const resolved = next instanceof Flare ? next : createFlare(next);
    this.#items[i] = resolved;
    this.#startedAt.delete(old);
    old.onDetach(this.#owner);
    resolved.onAttach(this.#owner);
    this.#sync();
    this.#owner.invalidate();
    return resolved;
  }

  clear(): void {
    for (const f of this.#items) f.onDetach(this.#owner);
    this.#items.length = 0;
    this.#sync();
    this.#owner.invalidate();
  }

  /** First attached flare of the given class, or undefined. */
  find<T extends Flare>(type: new (...args: never[]) => T): T | undefined {
    return this.#items.find((f): f is T => f instanceof type);
  }

  /** The furthest any enabled effect reaches outside the control. */
  get overflow(): number {
    let max = 0;
    for (const f of this.#items) if (f.enabled) max = Math.max(max, f.overflow);
    return max;
  }

  /** True while any enabled effect wants a frame every tick. */
  get animated(): boolean {
    return this.#items.some((f) => f.enabled && f.animated);
  }

  /**
   * Keep the owner's animator registration in step with what is attached.
   * Called on every change and once per frame, since `animated` can flip when
   * an animation finishes.
   */
  #sync(): void {
    const desktop = this.#owner.desktop;
    if (!desktop) {
      this.#animating = false;
      return;
    }
    const want = this.animated;
    if (want === this.#animating) return;
    this.#animating = want;
    if (want) desktop.addAnimator(this.#owner);
    else desktop.removeAnimator(this.#owner);
  }

  /** Re-evaluate the animator registration; called when the owner reattaches. */
  refresh(): void {
    this.#animating = false;
    this.#sync();
  }

  /**
   * Build the per-frame context and run `update` on each effect. Returns null
   * when there is nothing to paint.
   */
  frame(base: Omit<FlareContext, "time" | "delta">, now: number): FlareContext | null {
    if (this.#items.length === 0) return null;
    const delta = this.#lastFrame < 0 ? 0 : Math.max(0, now - this.#lastFrame);
    this.#lastFrame = now;
    // A shared context object per frame; `time` is re-stamped per flare below.
    return { ...base, time: now, delta };
  }

  /** Seconds since `flare` was attached. */
  timeFor(flare: Flare, now: number): number {
    let started = this.#startedAt.get(flare);
    if (started === undefined) {
      started = now;
      this.#startedAt.set(flare, now);
    }
    return now - started;
  }

  paintBelow(ctx: FlareContext, now: number): void {
    for (const f of this.#items) {
      if (!f.enabled) continue;
      const c = { ...ctx, time: this.timeFor(f, now) };
      f.update(c);
      f.paintBelow(c);
    }
    this.#sync();
  }

  paintAbove(ctx: FlareContext, now: number): void {
    for (const f of this.#items) {
      if (!f.enabled) continue;
      f.paintAbove({ ...ctx, time: this.timeFor(f, now) });
    }
  }
}
